import { useState } from "react"
import { VisuallyHidden } from "@radix-ui/react-visually-hidden"

import { Button } from "@/components/ui/button"
import {
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
  ResponsiveDrawer,
} from "@/components/ui/drawer"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"

/**
 * A preset list with a custom-range escape hatch, behind one trigger.
 *
 * **Presets are the product; custom is the escape hatch.** Most taps pick a
 * preset and are done — that row applies and closes the drawer immediately,
 * no second confirmation. Only the custom row asks for anything else: it
 * reveals two native `date` inputs and holds the drawer open until "Aplicar"
 * is pressed, because typing two dates is the one choice here worth letting
 * someone reconsider before it takes effect.
 *
 * **Two native `<input type="date">`, never a hand-rolled calendar.** A
 * calendar grid is a second date-picking UI to build, test and keep
 * accessible when the platform already ships one for free, and every mobile
 * OS renders its own native picker on top of the input regardless of what
 * this component draws around it.
 *
 * **`maxDate` makes a future date unpickable rather than explained.** It is
 * the caller's "today" — usually the tenant's own clock, not the browser's —
 * passed straight through as the `max` attribute on both inputs, so the
 * platform's own date picker greys the days out instead of this component
 * validating them after the fact.
 *
 * **`maxSpanDays` is checked here because it cannot be made unpickable.** The
 * ceiling is a property of the PAIR of dates, so no `max` attribute can
 * express it and the platform picker will happily offer both ends. A server
 * that silently degrades an over-long range to a preset is exactly right for
 * a link someone was sent — nobody typed it, so there is nothing to correct —
 * but wrong for a form the person in front of it just filled in while reading
 * the rule written underneath. So the rule that is printed is the rule that is
 * enforced: over the ceiling, "Aplicar" stays disabled and says why.
 *
 * **This is a `ResponsiveDrawer`, not a `ResponsiveDialog`.** The two are not
 * interchangeable here: `ResponsiveDialog` seeds `useState(open ?? false)`
 * once and never resyncs from a later `open` prop change, and a controlled
 * trigger that can be reopened by its caller needs the prop to keep working
 * after the first render. `ResponsiveDrawer` forwards `open`/`onOpenChange`
 * straight to the underlying primitive with no state of its own, so it stays
 * correct however the caller drives it. It also renders the same drawer
 * shape at every breakpoint — bottom sheet on mobile, side panel on desktop —
 * rather than swapping to a centered dialog, which is the shape a preset
 * list plus a form wants on a wide screen too.
 *
 * **Selection is reported through two callbacks, not one tagged union
 * setter**, because the two cases behave differently enough to want
 * different call sites: `onSelectPreset` fires on tap with no further input,
 * `onApplyCustom` fires only once both dates are filled and valid. A single
 * `onChange(value)` would still be correct, but it would push the "did the
 * user actually mean to submit this" question onto every caller instead of
 * answering it once, here.
 */

export const DATE_RANGE_CUSTOM_VALUE = "custom"

export type DateRangePickerOption = {
  value: string
  label: string
  /** Overrides the row's default `${testId}-option-${value}` test id. */
  testId?: string
}

export type DateRangePickerValue =
  | { kind: "preset"; preset: string }
  | { kind: "custom"; from: string; to: string }

export type DateRangePickerCopy = {
  title: string
  /** Label for the trailing "custom range" radio row. */
  custom: string
  from: string
  to: string
  apply: string
  /** Shown under the two date inputs, e.g. the max span and the future-date rule. */
  hint?: string
  /** Shown when `to` is before `from`. */
  invalid: string
  /** Shown when the span exceeds `maxSpanDays`. Required for that check to run. */
  tooLong?: string
}

export type DateRangePickerProps = {
  presets: DateRangePickerOption[]
  value: DateRangePickerValue
  onSelectPreset: (preset: string) => void
  onApplyCustom: (from: string, to: string) => void
  /** ISO `YYYY-MM-DD`. Passed as `max` on both date inputs. */
  maxDate: string
  /** Inclusive ceiling on `to - from`. Needs `copy.tooLong` to be enforced. */
  maxSpanDays?: number
  copy: DateRangePickerCopy
  trigger: React.ReactNode
  /** Prefixes every test id this component renders. */
  testId?: string
}

function radioValueFor(value: DateRangePickerValue): string {
  return value.kind === "custom" ? DATE_RANGE_CUSTOM_VALUE : value.preset
}

/**
 * Inclusive, and counted in UTC on purpose. Both ends are `YYYY-MM-DD` with no
 * time at all, so parsing them as UTC midnight makes the subtraction a plain
 * count of days; parsing them locally would put a DST boundary inside the
 * difference and drop or add an hour that has no business being here. Mirrors
 * `Staff::ReportPeriod::CustomRange.span_days`, which is `(to - from) + 1`.
 */
function spanDays(from: string, to: string): number {
  const day = 24 * 60 * 60 * 1000
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / day) + 1
}

export function DateRangePicker({
  presets,
  value,
  onSelectPreset,
  onApplyCustom,
  maxDate,
  maxSpanDays,
  copy,
  trigger,
  testId = "date-range-picker",
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false)
  const [radioValue, setRadioValue] = useState(() => radioValueFor(value))
  const [from, setFrom] = useState(value.kind === "custom" ? value.from : "")
  const [to, setTo] = useState(value.kind === "custom" ? value.to : "")

  // Resynced only at the moment the drawer opens, never while it is open. An
  // owner mid-way through typing a custom range must not have that undone by
  // an unrelated re-render of the page they are looking at underneath.
  function handleOpenChange(next: boolean) {
    if (next) {
      setRadioValue(radioValueFor(value))
      setFrom(value.kind === "custom" ? value.from : "")
      setTo(value.kind === "custom" ? value.to : "")
    }
    setOpen(next)
  }

  function handleRadioChange(next: string) {
    setRadioValue(next)
    if (next === DATE_RANGE_CUSTOM_VALUE) return

    onSelectPreset(next)
    setOpen(false)
  }

  const invalid = Boolean(from) && Boolean(to) && to < from
  const tooLong =
    !invalid &&
    Boolean(from) &&
    Boolean(to) &&
    maxSpanDays !== undefined &&
    copy.tooLong !== undefined &&
    spanDays(from, to) > maxSpanDays
  const canApply = Boolean(from) && Boolean(to) && !invalid && !tooLong

  function handleApply() {
    if (!canApply) return

    onApplyCustom(from, to)
    setOpen(false)
  }

  return (
    <ResponsiveDrawer open={open} onOpenChange={handleOpenChange}>
      <DrawerTrigger asChild data-testid={`${testId}-trigger`}>
        {trigger}
      </DrawerTrigger>
      <DrawerContent data-testid={`${testId}-drawer`}>
        <DrawerHeader>
          <DrawerTitle>{copy.title}</DrawerTitle>
          {/* Radix requires a description for its dialog role; the hint is the
              closest thing this drawer has to one, and hiding it here avoids
              printing it twice when it is also shown beside the date inputs. */}
          <VisuallyHidden>
            <DrawerDescription>{copy.hint ?? copy.title}</DrawerDescription>
          </VisuallyHidden>
        </DrawerHeader>

        <div className="space-y-4 overflow-y-auto px-4 pb-4">
          <RadioGroup
            value={radioValue}
            onValueChange={handleRadioChange}
            className="gap-2"
            data-testid={`${testId}-options`}
          >
            {presets.map((option) => (
              <label
                key={option.value}
                htmlFor={`${testId}-option-${option.value}`}
                className="flex h-11 cursor-pointer items-center gap-3 rounded-md border border-input px-3 text-sm has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5 md:h-9"
              >
                <RadioGroupItem
                  value={option.value}
                  id={`${testId}-option-${option.value}`}
                  data-testid={option.testId ?? `${testId}-option-${option.value}`}
                />
                {option.label}
              </label>
            ))}

            <label
              htmlFor={`${testId}-option-${DATE_RANGE_CUSTOM_VALUE}`}
              className="flex h-11 cursor-pointer items-center gap-3 rounded-md border border-input px-3 text-sm has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5 md:h-9"
            >
              <RadioGroupItem
                value={DATE_RANGE_CUSTOM_VALUE}
                id={`${testId}-option-${DATE_RANGE_CUSTOM_VALUE}`}
                data-testid={`${testId}-option-${DATE_RANGE_CUSTOM_VALUE}`}
              />
              {copy.custom}
            </label>
          </RadioGroup>

          {radioValue === DATE_RANGE_CUSTOM_VALUE ? (
            <div className="space-y-3 border-t border-border pt-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor={`${testId}-from`}>{copy.from}</Label>
                  <Input
                    id={`${testId}-from`}
                    type="date"
                    value={from}
                    max={maxDate}
                    aria-invalid={invalid}
                    onChange={(event) => setFrom(event.target.value)}
                    className="tabular-nums"
                    data-testid={`${testId}-from`}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`${testId}-to`}>{copy.to}</Label>
                  <Input
                    id={`${testId}-to`}
                    type="date"
                    value={to}
                    max={maxDate}
                    aria-invalid={invalid}
                    onChange={(event) => setTo(event.target.value)}
                    className="tabular-nums"
                    data-testid={`${testId}-to`}
                  />
                </div>
              </div>

              {copy.hint ? <p className="text-xs text-muted-foreground">{copy.hint}</p> : null}

              {invalid ? (
                <p className="text-xs text-destructive" data-testid={`${testId}-invalid`}>
                  {copy.invalid}
                </p>
              ) : null}

              {tooLong ? (
                <p className="text-xs text-destructive" data-testid={`${testId}-too-long`}>
                  {copy.tooLong}
                </p>
              ) : null}

              <Button
                type="button"
                className="w-full"
                disabled={!canApply}
                onClick={handleApply}
                data-testid={`${testId}-apply`}
              >
                {copy.apply}
              </Button>
            </div>
          ) : null}
        </div>
      </DrawerContent>
    </ResponsiveDrawer>
  )
}
